import { IconButton, Drawer as MuiDrawer, DrawerProps as MuiDrawerProps, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from 'components/divider';
import { X } from 'lucide-react';
import React, { useCallback, useState } from 'react';

interface DrawerProps extends Omit<MuiDrawerProps, 'title' | 'onClose' | 'open'> {
  title: React.ReactNode;
  button: React.ReactElement;
  actionButtons?: React.ReactNode;
  onClose?: () => void;
}

export type DrawerHandle = { close: () => void };

function Drawer(
  { sx, anchor = 'right', onClose, children, title, button, actionButtons }: DrawerProps,
  ref: React.ForwardedRef<DrawerHandle>
) {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  const close = useCallback(() => {
    onClose?.();
    setOpen(false);
  }, [onClose]);

  React.useImperativeHandle(ref, () => ({ close }), [close]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
  };

  return (
    <>
      {React.cloneElement(button, {
        onClick: (event: any) => {
          button.props.onClick?.(event);
          handleOpen(event);
        },
      })}

      <MuiDrawer
        sx={sx}
        anchor={anchor}
        open={open}
        slotProps={{
          paper: {
            sx: {
              padding: 8,
            },
          },
        }}>
        <Box role="presentation" display={'flex'} flexDirection={'column'} gap={4}>
          <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} gap={4}>
            <Box display={'flex'} gap={5} alignItems={'center'}>
              <IconButton onClick={close} sx={{ background: theme.palette.lightBlue.o75 }} disableRipple>
                <X />
              </IconButton>
              <Typography component="div" fontWeight={400} fontSize={18}>
                {title}
              </Typography>
            </Box>
            {actionButtons}
          </Box>
          <Divider flexItem dark />
          <Box display={'flex'}>{children}</Box>
        </Box>
      </MuiDrawer>
    </>
  );
}

export default React.forwardRef<DrawerHandle, DrawerProps>(Drawer);
